import { defineStore } from 'pinia'
import Api from '../api/Api'

export const useServiceStore = defineStore('service', {
  state: () => ({
    services: [],
    utilityPods: [],
    utilityNodes: [],
    limits: null,
  }),
  getters: {
    getLimits: function(state) {
      return state.limits;
    },
    getAllServices: function(state) {
      return state.services;
    },
    getRekognition: function(state) {
      const lookingFor = ['aws-detecting-text', 'aws-detecting-labels'];
      const services = [];

      for (let i = 0; i < state.services.length; i++) {
        if (lookingFor.includes(state.services[i].alias)) {
          services.push(state.services[i]);
        }
      }

      return services;
    },
    getTextToSpeech: function(state) {
      // We currently don't use any service for speech synthesis. This method is provided for backwards compatibility
      // only - consider accessing *getSpeechToText* instead.
      return state.services.filter(x => x.usage === 'transcribe');
    },
    getSpeechToText: function(state) {
      return state.services.filter(x => x.usage === 'transcribe');
    },
    getTranslations: function(state) {
      const lookingFor = ['aws-translate', 'google-translate'];
      return state.services.filter(x => lookingFor.indexOf(x.alias) >= 0).reverse();
    },
    getExtractFrames: function(state) {
      return state.services.find(x => x.alias === 'extract-frames');
    },
    getMediaConverter: function(state) {
      return state.services.find(x => x.alias === 'media-converter');
    },
    getMediaTranscoder: function(state) {
      return state.services.find(x => x.alias === 'media-transcoder');
    },
    getUpdateThumbnail: function(state) {
      return state.services.find(x => x.alias === 'thumbnail-generator');
    },
    getImprintSubtitles: function(state) {
      return state.services.find(x => x.alias === 'imprint-subtitles');
    },
    getGeneratingSubtitles: function(state) {
      return state.services.find(x => x.alias === 'generating-subtitles');
    },
    getArchiveService: function(state) {
      return state.services.find(x => x.alias === 'aws-archive');
    },
    getExportFCPXML: function(state) {
      return state.services.find(x => x.alias === 'generate-FCPXML');
    },
    getUtilityPods: function(state) {
      return state.utilityPods;
    },
    getUtilityPodsErrors: function(state) {
      if (Array.isArray(state.utilityPods)) {
        return state.utilityPods.filter(x => x.error);
      }

      return [];
    },
    getUtilityNodesErrors: function(state) {
      if (Array.isArray(state.utilityNodes)) {
        return state.utilityNodes.filter(x => x.error);
      }

      return [];
    },
    getRunnablePods: function(state) {
      if (state.utilityPods && Array.isArray(state.utilityPods)) {
        return state.utilityPods.filter(x => (x.info && x.info.runnable));
      }

      return [];
    },
    getRunnableNodes: function(state) {
      if (state.utilityNodes && Array.isArray(state.utilityNodes)) {
        return state.utilityNodes.filter(x => (x.info && x.info.runnable));
      }

      return [];
    },
    isPodRunnable: function(state) {
      return function(pod) {

        if (!Array.isArray(state.utilityPods)) {
          return false;
        }

        const utilityPod = state.utilityPods.find(function (x) {
          return x && x.pod && x.pod.id && x.pod.id == pod.id;
        });

        return utilityPod && utilityPod.info && utilityPod.info.runnable;
      };
    },
    getUtilityPod: function(state) {
      return function(pod) {
        if (!Array.isArray(state.utilityPods)) {
          return false;
        }

        return state.utilityPods.find(function (x) {
          return x && x.pod && x.pod.id && x.pod.id == pod.id;
        });
      }
    }
  },
  actions: {
    refreshServices: async function() {
      this.utilityPods = [];
      try {
        const response = await Api.service.getAllServices();
        this.services = response.data;
        return response.data;
      } catch (error) {
        console.log(error);
      }
    },
    resetUtilityPods: function() {
      this.utilityPods = [];
    },
    resetUtilityNodes: function() {
      this.utilityNodes = [];
    },
    updatePodsUtility: async function(payload) {
      const serviceAlias = payload.serviceAlias;
      const nodeIDs = [];
      const podIDs = [];
      const assetID = payload.assetID;
      const sourceLanguage = payload.sourcelanguage;
      const targetLanguage = payload.targetlanguage;
      const action = payload.action;

      if (payload.nodes) {
        for (let i = 0; i < payload.nodes.length; i++) {
          nodeIDs.push(payload.nodes[i].id);
        }
      }

      if (payload.pods) {
        for(let i = 0; i < payload.pods.length; i++) {
          podIDs.push(payload.pods[i].id);
        }
      }

      try {
        const response = await Api.service.getPodsUtility(serviceAlias, nodeIDs, podIDs, assetID, sourceLanguage, targetLanguage, action);
        if (!response.data) {
            response.data = {};
        }

        this.utilityPods = response.data.pods || [];
        this.utilityNodes = response.data.nodes || [];
        return response.data;
      } catch (error) {
        console.log("Error getting pods utility");
        console.log(error);
      }
    },
    runService: async function(payload) {
      const serviceAlias = payload.serviceAlias;
      const nodeIDs = [];
      const podIDs = [];

      if (!Array.isArray(payload.utilityNodes)) {
        payload.utilityNodes = [];
      }

      for(let i = 0; i < payload.utilityNodes.length; i++) {
        if (payload.utilityNodes[i].node) {
          nodeIDs.push(payload.utilityNodes[i].node.id);
        }
      }

      for(let i = 0; i < payload.utilityPods.length; i++) {
        if (payload.utilityPods[i].pod) {
          podIDs.push(payload.utilityPods[i].pod.id);
        }
      }

      const response = await Api.service.runService(serviceAlias, nodeIDs, podIDs, payload.params);

      if (response && response.data && response.data.pods) {
        this.utilityPods = response.data.pods || [];
      }
      if (response && response.data && response.data.nodes) {
        this.utilityNodes = response.data.nodes || [];
      }
      return response.data;
    },

    refreshServiceOptions: async function(serviceAlias) {
      const response = await Api.service.getServiceOptions(serviceAlias);
      return response.data;
    },

    refreshLimits: async function(payload) {
      const response = await Api.service.getResources(payload);
      this.limits = response.data;
    }
  },
});
